<template>
  <div class="o-step">
    <div class="flex items-center">
      <div>
        <div :class="circleClass">
          <template v-if="isComplete || isSuccess">
            <o-icon icon="check" :size="12" fill />
          </template>
          <template v-if="isActive">
            <div class="bg-primary-600 w-2 h-2 rounded-full dark:bg-primary-800" />
          </template>
        </div>
      </div>

      <div class="ml-4 w-full flex items-center justify-between">
        <div>
          <h3 v-if="name" :class="nameClass">
            {{ name }}
          </h3>
          <p v-if="info" class="text-gray-600 leading-none text-sm dark:text-gray-300">
            {{ info }}
          </p>
        </div>

        <div>
          <slot name="control" />
        </div>
      </div>
    </div>
    <div class="flex items-stretch">
      <div :class="lineClass" />
      <div class="ml-4 mt-4 mb-8 w-full">
        <slot v-bind="slotData" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface StepRegistration {
  state: string
}

interface Props {
  name?: string | null
  info?: string | null
  success?: boolean
  danger?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  name: null,
  info: null,
  success: false,
  danger: false,
})

const state = ref('disabled')

// Registration object exposed to parent Steps component
const registration = reactive<StepRegistration>({
  state: state.value,
})

// Sync parent mutations back to local state
watch(() => registration.state, (val) => { state.value = val })
watch(state, (val) => { registration.state = val })

// Inject register/unregister from parent Steps
const register = inject<((step: StepRegistration) => void) | null>('steps-register', null)
const unregister = inject<((step: StepRegistration) => void) | null>('steps-unregister', null)

onMounted(() => {
  register?.(registration)
})

onBeforeUnmount(() => {
  unregister?.(registration)
})

const slotData = computed(() => {
  return {
    state: state.value,
    isActive: isActive.value,
    isDisabled: isDisabled.value,
    isComplete: isComplete.value,
  }
})

const isActive = computed(() => state.value === 'active')
const isDisabled = computed(() => state.value === 'disabled')
const isComplete = computed(() => state.value === 'complete')
const isSuccess = computed(() => props.success)

const circleClass = computed(() => {
  const classes = ['w-6 h-6 md:w-8 md:h-8 rounded-full inline-flex items-center justify-center select-none']

  if (isComplete.value) {
    classes.push('border-2 border-primary-600 bg-primary-600 text-white dark:bg-primary-800 dark:border-primary-800')
  } else if (isActive.value) {
    classes.push('border-2 border-primary-600 text-primary-500 dark:text-primary-800 dark:border-primary-800')
  } else {
    classes.push('border-2 border-gray-200 text-gray-500 dark:text-gray-400 dark:border-gray-800')
  }

  return classes
})

const nameClass = computed(() => {
  const classes = ['font-semibold mb-1 text-sm uppercase leading-none']

  if (isComplete.value) {
    classes.push('text-gray-700 dark:text-gray-200')
  } else if (isActive.value) {
    classes.push('text-primary-600 dark:text-primary-700')
  } else {
    classes.push('text-gray-600 dark:text-gray-300')
  }

  return classes
})

const lineClass = computed(() => {
  const classes = ['o-step-line w-px mx-3 md:mx-4 border']

  if (isComplete.value) {
    classes.push('border-primary-500 dark:border-primary-800')
  } else {
    classes.push('border-dashed border-gray-200 dark:border-gray-800')
  }

  return classes
})

defineExpose({ state })
</script>

<style>
.o-step:last-child .o-step-line {
  opacity: 0;
}
</style>
